import React, { useState } from 'react';
import { useFlexibleList } from '../../../../hooks/useSWR';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';

const CapaEffectivenessAuditLogTab = ({ selectedRecord, config }) => {
  const { theme } = useTheme();
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  const { data: auditLogs = [], isLoading, error } = useFlexibleList(
    selectedRecord?.id
      ? `/audit-log/master/${config?.features?.masterEntityTypeId || 20}/${selectedRecord.id}`
      : null,
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getActionIcon = (action) => {
    switch (String(action || '').toUpperCase()) {
      case 'CREATE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'UPDATE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'DELETE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getActionColor = (action) => {
    switch (String(action || '').toUpperCase()) {
      case 'CREATE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DELETE':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFieldDisplayName = (fieldName) => {
    const fieldMap = {
      code: 'CAPA Number',
      relatedDeviationOrFinding: 'Related Deviation / Inspection Finding',
      capaDescription: 'CAPA Description',
      referenceDocument: 'Reference Document',
      statusId: 'Status',
      methodReviewFiles: 'Review of related files',
      methodReviewTraining: 'Review of training records',
      methodReviewComplaints: 'Review of complaints/deviations',
      methodProcessObservation: 'Process observation',
      methodOther: 'Other method',
      methodOtherNote: 'Other method note',
      criteriaCapaFullyImplemented: 'CAPA fully implemented',
      criteriaEn01Gd13Completed: 'EN 01 / ΓΔ.13 completed',
      criteriaVerificationDateSet: 'Verification date set',
      criteriaNoRecurrence: 'No recurrence',
      criteriaStaffTrained: 'Staff trained',
      criteriaEvidenceInQms: 'Evidence in QMS',
      evidenceCapaActionsCompleted: 'CAPA actions completed',
      evidenceTrainingRecords: 'Training records',
      evidenceUpdatedProcedures: 'Updated procedures',
      evidenceInternalAuditOrMonitoring: 'Internal audit / monitoring',
      evidenceOther: 'Other evidence',
      evidenceOtherNote: 'Other evidence note',
      evidenceNotes: 'Evidence notes',
      effectivenessResult: 'Effectiveness result',
      effectivenessResultNote: 'Effectiveness result note',
      decision: 'Decision',
      decisionNote: 'Decision note',
      checkedAt: 'Checked at',
      checkedById: 'Checked by',
      approvedAt: 'Approved at',
      approvedById: 'Approved by',
      closedAt: 'Closed at',
      closedById: 'Closed by',
      createdById: 'Created by',
      updatedById: 'Updated by',
      createdAt: 'Created date',
      updatedAt: 'Updated date',
    };

    return (
      fieldMap[fieldName] ||
      fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
    );
  };

  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined) return 'Not set';

    if (typeof value === 'object' && value !== null && value.displayValue !== undefined) {
      return value.displayValue;
    }

    if (
      fieldName.includes('At') ||
      fieldName.includes('Date') ||
      fieldName === 'createdAt' ||
      fieldName === 'updatedAt'
    ) {
      try {
        return formatDateTime(value);
      } catch {
        return String(value);
      }
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object' && value !== null) {
      if (value.displayValue !== undefined) return value.displayValue;
      if (value.id !== undefined) return value.id;
      return value.name || value.title || value.userDisplayName || 'Object';
    }

    if (typeof value === 'string' && value.length > 50) {
      return `${value.substring(0, 50)}...`;
    }

    return String(value);
  };

  const toggleExpanded = (logId) => {
    const next = new Set(expandedLogs);
    if (next.has(logId)) next.delete(logId);
    else next.add(logId);
    setExpandedLogs(next);
  };

  const renderFieldChanges = (
    oldValues,
    newValues,
    changedFields,
    logId,
    actionDetails,
    action,
  ) => {
    if (!changedFields || changedFields.length === 0) return null;

    const isExpanded = expandedLogs.has(logId);
    const isCreateAction = action === 'CREATE';

    return (
      <div className="mt-0">
        <button
          type="button"
          onClick={() => toggleExpanded(logId)}
          className={cn(
            'w-full text-left text-sm font-semibold mb-3 flex items-center justify-between hover:opacity-80 transition-opacity',
            theme.text.primary,
          )}
        >
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {isCreateAction ? 'Initial Values' : 'Changes Made'} ({changedFields.length})
          </div>
          <svg
            className={cn('w-4 h-4 transition-transform', isExpanded ? 'rotate-180' : '')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isExpanded && (
          <div className="space-y-3">
            {changedFields.map((field, index) => {
              const oldValue = oldValues && oldValues[field];
              const newValue = newValues && newValues[field];
              const fieldDisplayName = getFieldDisplayName(field);

              return (
                <div
                  key={index}
                  className={cn(
                    'p-3 rounded-lg border',
                    theme.border.subtle,
                    theme.colors.backgroundAlt,
                  )}
                >
                  <div className="mb-1">
                    <span className={cn('text-sm font-medium', theme.text.primary)}>
                      {fieldDisplayName}
                    </span>
                  </div>

                  {isCreateAction ? (
                    <div className="space-y-1">
                      <div className={cn('text-xs font-medium text-green-600', theme.text.secondary)}>
                        Initial Value
                      </div>
                      <div className="p-2 rounded border-l-4 border-green-400 bg-green-50 dark:bg-green-950/35 dark:border-green-500 text-sm text-green-900 dark:text-green-100">
                        {formatFieldValue(newValue, field)}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <div className={cn('text-xs font-medium text-red-600', theme.text.secondary)}>
                          Previous Value
                        </div>
                        <div className="p-2 rounded border-l-4 border-red-400 bg-red-50 dark:bg-red-950/35 dark:border-red-500 text-sm text-red-900 dark:text-red-100">
                          {formatFieldValue(oldValue, field)}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className={cn('text-xs font-medium text-green-600', theme.text.secondary)}>
                          New Value
                        </div>
                        <div className="p-2 rounded border-l-4 border-green-400 bg-green-50 dark:bg-green-950/35 dark:border-green-500 text-sm text-green-900 dark:text-green-100">
                          {formatFieldValue(newValue, field)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {actionDetails && (
              <div className="mt-4">
                <h4 className={cn('text-sm font-medium mb-2', theme.text.primary)}>
                  Action Details:
                </h4>
                <div
                  className={cn(
                    'p-3 rounded-lg text-xs',
                    theme.colors.backgroundAlt,
                    theme.border.subtle,
                  )}
                >
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(actionDetails, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={cn('flex-1 overflow-y-auto advanced-table-scroll p-4', theme.text.primary)}>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('flex-1 overflow-y-auto advanced-table-scroll p-4', theme.text.primary)}>
        <div className="text-center py-8">
          <div className={cn('text-red-600 mb-2', theme.text.primary)}>
            Error loading audit logs
          </div>
          <div className={cn('text-sm', theme.text.secondary)}>
            {error.message || 'Failed to fetch audit logs'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto advanced-table-scroll p-4 space-y-6',
        theme.text.primary,
      )}
    >
      {auditLogs.length === 0 ? (
        <div className={cn('text-center py-8', theme.text.muted)}>
          <svg
            className="w-12 h-12 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <div className={cn('text-lg font-medium mb-2', theme.text.primary)}>
            No Audit Logs
          </div>
          <div className={cn('text-sm', theme.text.secondary)}>
            No audit trail available for this CAPA effectiveness check yet.
          </div>
        </div>
      ) : (
        <div className="relative pl-8">
          <div className={cn('absolute left-4 top-0 bottom-0 w-0.5', theme.border.subtle)} />
          {auditLogs.map((log, index) => (
            <div key={log.id || index} className="relative mb-6">
              <div
                className={cn(
                  'absolute left-0 top-0 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center',
                  getActionColor(log.action),
                )}
              >
                {getActionIcon(log.action)}
              </div>
              <div
                className={cn(
                  'ml-8 p-4 rounded-lg border transition-colors duration-200',
                  theme.border.subtle,
                  theme.colors.background,
                  'hover:border-opacity-60',
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        getActionColor(log.action),
                      )}
                    >
                      {log.action}
                    </span>
                    {log.entityType?.name && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {log.entityType.name}
                      </span>
                    )}
                  </div>
                  <span className={cn('text-xs', theme.text.secondary)}>
                    {formatDateTime(log.performedAt)}
                  </span>
                </div>

                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {log.description && (
                      <div className={cn('text-sm mb-2', theme.text.secondary)}>
                        {log.description}
                      </div>
                    )}
                  </div>
                  <span className={cn('text-xs', theme.text.secondary)}>
                    {log.user?.userDisplayName || 'System'}
                  </span>
                </div>

                {renderFieldChanges(
                  log.oldValues,
                  log.newValues,
                  log.changedFields,
                  log.id,
                  log.actionDetails,
                  log.action,
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CapaEffectivenessAuditLogTab;
